// REST API v2 hooks for public data
// Based on web-frontend/src/api/usePublicV2.ts

import { useState, useEffect } from "react";

const REST_V2_API_BASE =
  import.meta.env.VITE_API_BASE_URL || "https://api-staging.withsplice.com";

export interface Promo {
  id: string;
  title: string;
  description: string;
  discount_type: "percentage" | "fixed";
  discount_value: number;
  start_date: string;
  end_date: string;
  is_active: boolean;
}

export interface Review {
  id: string;
  stars?: number;
  rating?: number;
  message?: string;
  comment?: string;
  client?: {
    id: string;
    name: string;
    email?: string;
  };
  client_name?: string;
  service_name?: string;
  created_at: string;
  updated_at?: string;
  response?: string;
  reply?: string;
  replied_at?: string;
  replies?: {
    id: string;
    author_type: "business" | "client" | string;
    body: string;
    created_at: string;
    updated_at?: string;
    user_id?: string;
    client_id?: string;
    client_account_id?: string;
  }[];
}

// Availability types for REST API v2
export interface AvailabilityService {
  service_id: string;
  staff_id?: string[];
  service_variant_id?: string;
}

export interface AvailabilityNextRequest {
  salon_id: string;
  services: AvailabilityService[];
  date?: string; // Starting date (YYYY-MM-DD) - search starts from day after this date
  _timestamp?: number; // Used to force re-fetch even with identical parameters
}

export interface AvailabilityNextSlot {
  date: string;
  start_time: string;
  end_time: string;
  timezone: string;
}

export interface AvailabilityNextResponse {
  next_available_slot?: AvailabilityNextSlot;
}

export const useGetPublicSalonPromos = (salonId: string | null) => {
  const [data, setData] = useState<Promo[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    setData(null);
    setError(null);
    if (!salonId) return;

    let active = true;

    const fetchPromos = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${REST_V2_API_BASE}/v2/salons/${salonId}/promos`
        );

        if (response.status === 404) {
          if (active) setData([]);
          return;
        }

        if (!response.ok) {
          console.error(
            "[Promos] Response not OK:",
            response.status,
            response.statusText
          );
          throw new Error(`Failed to fetch promos: ${response.status}`);
        }

        const promos = await response.json();

        if (active) setData(promos);
      } catch (err) {
        console.error("[Promos] Error fetching:", err);
        if (active) {
          setError(err as Error);
          setData([]);
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchPromos();

    return () => {
      active = false;
    };
  }, [salonId]);

  return { data, loading, error };
};

export const useGetPublicSalonReviews = (salonId: string | null) => {
  const [data, setData] = useState<Review[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    setData(null);
    setError(null);
    if (!salonId) return;

    let active = true;

    const fetchReviews = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${REST_V2_API_BASE}/v2/salons/${salonId}/reviews`
        );

        if (response.status === 404) {
          if (active) setData([]);
          return;
        }

        if (!response.ok) {
          throw new Error(`Failed to fetch reviews: ${response.status}`);
        }

        const reviews = await response.json();

        if (active) setData(reviews);
      } catch (err) {
        console.error("[Reviews] Error fetching:", err);
        if (active) {
          setError(err as Error);
          setData([]);
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchReviews();

    return () => {
      active = false;
    };
  }, [salonId]);

  return { data, loading, error };
};

export const useGetAvailabilityNext = (
  request: AvailabilityNextRequest | null
) => {
  const [data, setData] = useState<AvailabilityNextResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Stringify request to use as a stable dependency for the effect
  const requestKey = request ? JSON.stringify(request) : null;

  useEffect(() => {
    if (!request || !requestKey) return;

    const fetchAvailability = async () => {
      setLoading(true);
      setError(null);
      try {
        // Build query parameters
        const params = new URLSearchParams();
        params.append("services", JSON.stringify(request.services));
        if (request.date) {
          params.append("date", request.date);
        }

        const response = await fetch(
          `${REST_V2_API_BASE}/v2/salons/${
            request.salon_id
          }/availability/next?${params.toString()}`
        );

        if (response.status === 404) {
          setData({});
          return;
        }

        if (!response.ok) {
          throw new Error(`Failed to fetch availability: ${response.status}`);
        }

        const availability = await response.json();
        setData(availability);
      } catch (err) {
        console.error("[Availability] Error fetching:", err);
        setError(err as Error);
        setData({}); // Set empty response on error
      } finally {
        setLoading(false);
      }
    };

    fetchAvailability();
  }, [requestKey]);

  return { data, loading, error };
};
